import React, { useState, useEffect } from 'react';
import ShapePreview from './ShapePreview';

const FIELD_TYPES = ['text', 'number', 'select', 'boolean'];

const AssetDefinitionEditor = ({ definition, onSave, onCancel }) => {
  const [def, setDef] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDef(JSON.parse(JSON.stringify(definition)));
    setDirty(false);
  }, [definition]);

  if (!def) return null;

  const update = (path, value) => {
    setDef((prev) => {
      const next = { ...prev };
      if (path.includes('.')) {
        const [p1, p2] = path.split('.');
        next[p1] = { ...next[p1], [p2]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
    setDirty(true);
  };

  // --- Field management ---
  const addField = () => {
    const id = 'f' + Date.now();
    const fields = [...def.fields, {
      id, name: '', label: '', type: 'text', defaultValue: '', required: false,
    }];
    setDef({ ...def, fields });
    setDirty(true);
  };

  const updateField = (fieldId, key, value) => {
    const fields = def.fields.map((f) =>
      f.id === fieldId ? { ...f, [key]: value } : f
    );
    setDef({ ...def, fields });
    setDirty(true);
  };

  const removeField = (fieldId) => {
    const fields = def.fields.filter((f) => f.id !== fieldId);
    setDef({ ...def, fields });
    setDirty(true);
  };

  const moveField = (fieldId, dir) => {
    const idx = def.fields.findIndex((f) => f.id === fieldId);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === def.fields.length - 1)) return;
    const fields = [...def.fields];
    [fields[idx], fields[idx + dir]] = [fields[idx + dir], fields[idx]];
    setDef({ ...def, fields });
    setDirty(true);
  };

  // --- Connector management ---
  const toggleConnectorFavorite = (connectorId) => {
    const connectors = def.connectors.map((c) =>
      c.id === connectorId ? { ...c, favorite: !c.favorite } : c
    );
    setDef({ ...def, connectors });
    setDirty(true);
  };

  const toggleConnectorEnabled = (connectorId) => {
    const connectors = def.connectors.map((c) =>
      c.id === connectorId ? { ...c, enabled: !c.enabled, favorite: !c.enabled ? c.favorite : false } : c
    );
    setDef({ ...def, connectors });
    setDirty(true);
  };

  const favoriteCount = def.connectors.filter((c) => c.favorite).length;
  const enabledCount = def.connectors.filter((c) => c.enabled).length;

  return (
    <div className="def-editor">
      {/* Header */}
      <div className="def-editor-header">
        <div className="def-editor-title">
          <span className="def-editor-icon">{def.icon}</span>
          <div>
            <h2>{def.name}</h2>
            <span className="def-editor-id">ID: {def.id} &middot; {def.category}</span>
          </div>
        </div>
        <div className="def-editor-actions">
          <button className="btn-ghost" onClick={onCancel}>{'← Back'}</button>
          <button
            className="btn-accent"
            onClick={() => { onSave(def); setDirty(false); }}
            disabled={!dirty}
          >
            {dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      <div className="def-editor-body">
        {/* LEFT: Fields / Database Definition */}
        <div className="def-editor-left">
          {/* Basic info */}
          <div className="def-section">
            <h3>Asset Properties</h3>
            <div className="def-form-row">
              <div className="def-form-group">
                <label>Name</label>
                <input value={def.name} onChange={(e) => update('name', e.target.value)} />
              </div>
              <div className="def-form-group" style={{ width: 80 }}>
                <label>Icon</label>
                <input
                  value={def.icon}
                  onChange={(e) => update('icon', e.target.value)}
                  className="icon-input"
                />
              </div>
            </div>
            <div className="def-form-row">
              <div className="def-form-group">
                <label>ID (database key)</label>
                <input value={def.id} onChange={(e) => update('id', e.target.value)} className="mono" />
              </div>
              <div className="def-form-group">
                <label>Category</label>
                <select value={def.category} onChange={(e) => update('category', e.target.value)}>
                  <option value="generation">Generation</option>
                  <option value="storage">Storage</option>
                  <option value="consumption">Consumption</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="grid">Grid</option>
                </select>
              </div>
            </div>
          </div>

          {/* Field definitions = database columns */}
          <div className="def-section">
            <div className="def-section-header">
              <h3>Database Fields</h3>
              <button className="btn-sm-accent" onClick={addField}>+ Add Field</button>
            </div>
            <div className="def-field-list">
              {def.fields.map((field, idx) => (
                <div key={field.id} className="def-field-row">
                  <div className="def-field-handle">
                    <button
                      className="btn-move"
                      onClick={() => moveField(field.id, -1)}
                      disabled={idx === 0}
                      title="Move up"
                    >▲</button>
                    <button
                      className="btn-move"
                      onClick={() => moveField(field.id, 1)}
                      disabled={idx === def.fields.length - 1}
                      title="Move down"
                    >▼</button>
                  </div>
                  <div className="def-field-inputs">
                    <input
                      className="field-name mono"
                      placeholder="field_name"
                      value={field.name}
                      onChange={(e) => updateField(field.id, 'name', e.target.value)}
                    />
                    <input
                      className="field-label"
                      placeholder="Label"
                      value={field.label}
                      onChange={(e) => updateField(field.id, 'label', e.target.value)}
                    />
                    <select
                      className="field-type"
                      value={field.type}
                      onChange={(e) => updateField(field.id, 'type', e.target.value)}
                    >
                      {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input
                      className="field-default"
                      placeholder={field.type === 'select' ? 'opt1, opt2, ...' : 'Default'}
                      value={field.type === 'select' ? (field.options || []).join(', ') : field.defaultValue}
                      onChange={(e) => {
                        if (field.type === 'select') {
                          updateField(field.id, 'options', e.target.value.split(',').map((s) => s.trim()).filter(Boolean));
                        } else {
                          updateField(field.id, 'defaultValue', e.target.value);
                        }
                      }}
                    />
                    {field.unit !== undefined || field.type === 'number' ? (
                      <input
                        className="field-unit"
                        placeholder="unit"
                        value={field.unit || ''}
                        onChange={(e) => updateField(field.id, 'unit', e.target.value)}
                      />
                    ) : null}
                    <label className="field-required-toggle" title="Required">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(field.id, 'required', e.target.checked)}
                      />
                      <span>Req</span>
                    </label>
                  </div>
                  <button
                    className="btn-remove-field"
                    onClick={() => removeField(field.id)}
                    title="Remove field"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              ))}
              {def.fields.length === 0 && (
                <div className="def-empty">No fields defined. Click "+ Add Field" to add database columns.</div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Shape & Connectors */}
        <div className="def-editor-right">
          {/* Shape config */}
          <div className="def-section">
            <h3>Shape Configuration</h3>
            <div className="def-form-row">
              <div className="def-form-group">
                <label>Width (px)</label>
                <input
                  type="number"
                  value={def.shape.width}
                  onChange={(e) => update('shape.width', parseInt(e.target.value) || 100)}
                  min={60}
                  max={400}
                />
              </div>
              <div className="def-form-group">
                <label>Height (px)</label>
                <input
                  type="number"
                  value={def.shape.height}
                  onChange={(e) => update('shape.height', parseInt(e.target.value) || 60)}
                  min={40}
                  max={400}
                />
              </div>
            </div>
            <div className="def-form-row">
              <div className="def-form-group">
                <label>Frame Color</label>
                <div className="color-input-wrap">
                  <input
                    type="color"
                    value={def.frameColor}
                    onChange={(e) => update('frameColor', e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={def.frameColor}
                    onChange={(e) => update('frameColor', e.target.value)}
                    className="color-hex mono"
                  />
                </div>
              </div>
              <div className="def-form-group">
                <label>Connector Color</label>
                <div className="color-input-wrap">
                  <input
                    type="color"
                    value={def.connectorColor}
                    onChange={(e) => update('connectorColor', e.target.value)}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={def.connectorColor}
                    onChange={(e) => update('connectorColor', e.target.value)}
                    className="color-hex mono"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Shape preview */}
          <div className="def-section">
            <h3>Preview</h3>
            <div className="shape-preview-container">
              <ShapePreview
                width={def.shape.width}
                height={def.shape.height}
                frameColor={def.frameColor}
                connectorColor={def.connectorColor}
                connectors={def.connectors}
                icon={def.icon}
                name={def.name}
                interactive={true}
                onConnectorClick={toggleConnectorFavorite}
                scale={1}
              />
            </div>
            <div className="shape-preview-hint">
              Click a connector to toggle ★ favorite. Favorites are pre-selected when placed on the canvas.
            </div>
          </div>

          {/* Connector config */}
          <div className="def-section">
            <div className="def-section-header">
              <h3>Connectors</h3>
              <span className="connector-stats">
                {enabledCount} enabled &middot; {favoriteCount} favorites
              </span>
            </div>
            <div className="def-form-group">
              <label>Max Selectable Connectors (on canvas)</label>
              <input
                type="number"
                value={def.maxSelectableConnectors}
                onChange={(e) => update('maxSelectableConnectors', parseInt(e.target.value) || 1)}
                min={1}
                max={12}
              />
            </div>
            <div className="connector-grid">
              {['top', 'right', 'bottom', 'left'].map((side) => (
                <div key={side} className="connector-side">
                  <span className="connector-side-label">{side}</span>
                  {def.connectors
                    .filter((c) => c.side === side)
                    .map((c) => (
                      <div key={c.id} className="connector-toggle-row">
                        <label className="connector-toggle">
                          <input
                            type="checkbox"
                            checked={c.enabled}
                            onChange={() => toggleConnectorEnabled(c.id)}
                          />
                          <span className="connector-pos">{c.position + 1}</span>
                        </label>
                        <button
                          className={`btn-fav ${c.favorite ? 'active' : ''}`}
                          onClick={() => toggleConnectorFavorite(c.id)}
                          disabled={!c.enabled}
                          title="Toggle favorite"
                        >
                          ★
                        </button>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDefinitionEditor;
